<template>

  <div class="mt-4">
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-light" role="status"></div>
      <p>Cargando...</p>
    </div>
    <!-- Page Header -->
    <div class="page-header">
      <h1 class="page-header-title"><i class="bi bi-building me-2"></i>Listado de Envíos Locales — Mercadería</h1>
    </div>

    <!-- Filtros -->
    <div class="filter-card">
      <div class="row g-2">
        <div class="col-md-2 d-none d-md-block">
          <label class="form-label">RUC</label>
          <input type="text" class="form-control form-control-sm" v-model="filtros.ruc" placeholder="Ingrese RUC" />
        </div>
        <div class="col-md-3 d-none d-md-block">
          <label class="form-label">Razón Social</label>
          <input type="text" class="form-control form-control-sm" v-model="filtros.razon_social" placeholder="Razón social..." />
        </div>
        <div class="col-md-3 d-none d-md-block">
          <label class="form-label">Cliente Final</label>
          <input type="text" class="form-control form-control-sm" v-model="filtros.cliente_final" placeholder="Cliente final..." />
        </div>
        <div class="col-md-2 d-none d-md-block">
          <label class="form-label">N° Pedido</label>
          <input type="text" class="form-control form-control-sm" v-model="filtros.pedido" placeholder="N° Pedido" />
        </div>
        <div class="col-md-2 d-none d-md-block">
          <label class="form-label">N° Orden Compra</label>
          <input type="text" class="form-control form-control-sm" v-model="filtros.orden_compra" placeholder="N° Orden" />
        </div>
        <div class="col-12 col-md-2">
          <label class="form-label">N° Guía</label>
          <input type="text" class="form-control form-control-sm" v-model="filtros.guia" placeholder="N° Guía" />
        </div>
        <div class="col-md-2 d-none d-md-block">
          <label class="form-label">Serie de Guía</label>
          <input type="text" class="form-control form-control-sm" v-model="filtros.serie_guia" placeholder="Ej: T012" />
        </div>
        <div class="col-12 col-md-3">
          <label class="form-label">Rango de Fechas</label>
          <div class="row gx-1">
            <div class="col-6"><input type="date" class="form-control form-control-sm" v-model="filtros.fecha_inicio" /></div>
            <div class="col-6"><input type="date" class="form-control form-control-sm" v-model="filtros.fecha_fin" /></div>
          </div>
        </div>
        <div class="col-md-3 d-none d-md-block">
          <label class="form-label">Estado</label>
          <select class="form-select form-select-sm" v-model="filtros.estado_entrega">
            <option value="">Todos</option>
            <option v-for="item in estadosEnvio" :value="item.id" :key="item.id">{{ item.nombre }}</option>
          </select>
        </div>
        <div class="col-md-2 d-none d-md-block" v-if="esSuperAdmin()">
          <label class="form-label">Modalidad</label>
          <select class="form-select form-select-sm" v-model="filtros.modalidad_envio">
            <option value="">Todos</option>
            <option value="CONSOLIDADO">CONSOLIDADO</option>
            <option value="EXCLUSIVO">EXCLUSIVO</option>
          </select>
        </div>
        <div class="col-12 col-md-3" v-if="esSuperAdmin()">
          <label class="form-label">Vehículo</label>
          <select class="form-select form-select-sm" v-model="filtros.id_vehiculo">
            <option value="">Todos</option>
            <option :value="v.id_vehiculo" v-for="v in vehiculos" :key="v.id_vehiculo">{{ v.nombre_vehiculo }}</option>
          </select>
        </div>
        <!-- Los botones se ubican en la última fila de filtros si hay espacio (una fila menos) -->
        <div class="col-12 col-md-auto ms-auto d-flex justify-content-end align-items-end">
          <div class="filter-actions">
            <button class="btn-filter" @click="aplicarFiltros"><i class="bi bi-search"></i> Buscar</button>
            <button class="btn-clear" @click="limpiarFiltros()"><i class="bi bi-x-circle"></i> Limpiar</button>
            <button class="btn-export" @click="exportarExcel" :disabled="exportando" title="Exporta hasta 2 meses (facturación mensual)">
              <span v-if="exportando" class="spinner-border spinner-border-sm" role="status"></span>
              <i v-else class="bi bi-file-earmark-excel"></i>
              {{ exportando ? 'Exportando…' : 'Exportar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="text-center text-muted py-3" v-if="envios.length === 0"> No se encontraron registros.</div>

    <!-- VERSIÓN DE TABLA (solo para pantallas medianas o más grandes) -->
    <div class="table-responsive text-nowrap d-none d-md-block">
      <table class="table table-font-small table-bordered">
        <thead>
          <tr>
            <th>Rastreo</th>
            <th>Fecha Salida</th>
            <th>Fecha Entrega</th>
            <th>N° Guía</th>
            <th>N° Pedido</th>
            <th>N° Factura</th>
            <th>N° Orden de Compra</th>
            <th>RUC Remitente</th>
            <th>Remitente</th>
            <th>RUC Destinatario</th>
            <th>Destinatario</th>
            <th>Estado</th>
            <th class="col-acciones">Acciones</th>
          </tr>
        </thead>

        <tbody class="table-border-bottom-0 padding-custom">
          <tr v-for="(envio, index) in envios" :key="index">
            <td class="celda-id"><a :href="`/tracking-local?numeroRastreo=${envio.rastreo}`">
                {{ envio.rastreo }}
              </a>
            </td>
            <td class="celda-id">{{ formatFecha(envio.fecha_salida) }}</td>
            <td class="celda-id">{{ formatFecha(envio.fecha_entrega) }}</td>
            <td class="celda-guia">{{ envio.detalles.length > 1 ? 'VARIAS GUÍAS' : envio.guias_concatenadas }}</td>
            <td>{{ envio.detalles.length > 1 ? 'VARIOS PEDIDOS' : envio.pedidos_concatenados }}</td>
            <td>{{ envio.detalles.length > 1 ? 'VARIAS FACTURAS' : envio.facturas_concatenadas }}</td>
            <td>{{ envio.detalles.length > 1 ? 'VARIAS OC' : envio.oc_concatenadas }}</td>
            <td class="celda-id">{{ envio.ruc_remitente }}</td>
            <td class="celda-entidad"><div class="entidad-nombre" :title="envio.empresa_sucursal">{{ envio.empresa_sucursal }}</div></td>
            <td class="celda-id">{{ envio.ruc_destinatario }}</td>
            <td class="celda-entidad"><div class="entidad-nombre" :title="envio.empresa_sucursal_destino">{{ envio.empresa_sucursal_destino }}</div></td>
            <td class="p-1">
              <span :class="getBadgeClass(envio.estado_entrega)" @click="openEstadoEnvio(envio)">
                {{ envio.estado_envio }}
              </span>
            </td>
            <td class="col-acciones position-relative">
              <div class="btn-group">
                <button class="btn-acciones dropdown-toggle" type="button" title="Acciones" aria-label="Acciones" data-bs-toggle="dropdown"
                  aria-expanded="false">
                  <i class="bi bi-three-dots-vertical"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end custom-dropdown" data-bs-popper="static">
                  <li>
                    <a class="dropdown-item" href="#" @click.prevent="mostrarDetalle(envio)">
                      <i class="bi bi-search text-dark"></i>
                      Mostrar detalles
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item" href="#" @click.prevent="editarEnvio(envio)">
                      <i class="bi bi-pencil text-dark"></i>
                      Editar envío
                    </a>
                  </li>
                  <li v-if="esSuperAdmin()">
                    <a class="dropdown-item" href="#" @click.prevent="abrirGestionarModal(envio)">
                      <i class="bi bi-list-task text-dark"></i>
                      Gestionar
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item" href="#" @click.prevent="eliminarEnvio(envio)">
                      <i class="bi bi-trash text-dark"></i>
                      Eliminar
                    </a>
                  </li>
                  <li v-if="esSuperAdmin()">
                    <a class="dropdown-item" href="#" @click.prevent="abrirCargarArchivosModal(envio)">
                      <i class="bi bi-upload text-dark"></i>
                      Documentos
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item" href="#" @click.prevent="abrirGestionGuiasModal(envio)">
                      <i class="bi bi-upload text-dark"></i>
                      Gestión de Guías
                    </a>
                  </li>
                </ul>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

    </div>

    <!-- VERSIÓN DE CARD (solo para móviles) -->
    <div class="d-block d-md-none">
      <div v-for="(envio, index) in envios" :key="index" class="card mb-2">
        <div class="card-body p-2">
          <div @click.prevent="mostrarDetalle(envio)">
            <h6 class="card-title fw-bold mb-2">Rastreo: {{ envio.rastreo }}</h6>
            <p class="mb-1"><strong>Detalle:</strong> {{ envio.detalles[0]?.detalle_metodo_envio }}</p>

            <p class="mb-1"><strong>Fecha Entrega:</strong> {{ formatFecha(envio.fecha_entrega) }} {{
              formatHoraAMPM(envio.hora_llegada_maxima) }}</p>
            <p class="mb-1"><strong>N° Guía:</strong> {{ envio.guias_concatenadas }}</p>
            <p class="mb-1"><strong>N° Factura:</strong> {{ envio.facturas_concatenadas }}</p>
            <p class="mb-1"><strong>Remitente:</strong> {{ envio.empresa_sucursal }}</p>
            <p class="mb-1"><strong>Destinatario:</strong> {{ envio.empresa_sucursal_destino }}</p>
          </div>
          <p class="mb-1"><strong>Estado:</strong>
            <span :class="getBadgeClass(envio.estado_entrega)" class="mx-2" @click="openEstadoEnvio(envio)">
              {{ envio.estado_envio }}
            </span>
          </p>
          <div class="btn-group w-100 mt-2">
            <button class="btn btn-outline-dark btn-sm dropdown-toggle w-100" type="button" data-bs-toggle="dropdown">
              <i class="bi bi-three-dots-vertical"></i> Opciones
            </button>
            <ul class="dropdown-menu w-100">
              <li>
                <a class="dropdown-item" href="#" @click.prevent="mostrarDetalle(envio)">
                  <i class="bi bi-search text-dark"></i> Mostrar detalles
                </a>
              </li>
              <li v-if="esAdmin()">
                <a class="dropdown-item" href="#" @click.prevent="editarEnvio(envio)">
                  <i class="bi bi-pencil text-dark"></i> Editar envío
                </a>
              </li>
              <li v-if="esSuperAdmin()">
                <a class="dropdown-item" href="#" @click.prevent="abrirGestionarModal(envio)">
                  <i class="bi bi-list-task text-dark"></i> Gestionar
                </a>
              </li>
              <li v-if="esSuperAdmin()">
                <a class="dropdown-item" href="#" @click.prevent="abrirCargarArchivosModal(envio)">
                  <i class="bi bi-upload text-dark"></i> Cargar archivos
                </a>
              </li>
              <li>
                <a class="dropdown-item" href="#" @click.prevent="abrirGestionGuiasModal(envio)">
                  <i class="bi bi-file-text text-dark"></i> Gestión de Guías
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Paginación (25 por página, viene paginado desde el backend) -->
    <PaginacionListado :pagina="pagina" :total-paginas="totalPaginas" :total="totalEnvios" :por-pagina="porPagina"
      :en-pagina="envios.length" :cargando="isLoading" etiqueta="envíos" @cambiar="cambiarPagina" />


    <div v-if="modalGestionANW" id="modalGestionANW" class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog modal-xl">
        <div class="modal-content shadow-lg border-0">
          <!-- Encabezado -->
          <div class="modal-header">
            <h5 class="modal-title">Gestión del Envío</h5>
            <button type="button" class="btn-close" @click="modalGestionANW = false"></button>
          </div>
          <div class="modal-body p-4">
            <div class="mb-4">
              <ShippingANWLocal ref="shippingANWRef" :envio="detalleEnvio"></ShippingANWLocal>
            </div>
            <div class="mt-3 d-flex justify-content-end mb-3"><button @click="guardarGestion()" type="submit"
                class="btn btn-primary d-flex align-items-center gap-2"><i class="bi bi-floppy"></i> Guardar</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="modalDetalle" class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content shadow-lg border-0">
          <!-- Encabezado -->
          <div class="modal-header">
            <h5 class="modal-title">Detalles del Envío</h5>
            <button type="button" class="btn-close" @click="cerrarDetalle"></button>
          </div>

          <!-- Cuerpo del Modal -->
          <div class="modal-body p-4">
            <div class="d-none d-md-block">

              <!-- Datos Generales -->
              <div class="mb-4">
                <h6 class="fw-bold text-primary">Datos Generales</h6>
                <table class="table table-bordered table-sm">
                  <tbody>
                    <tr>
                      <td><strong>N° de Rastreo:</strong><br><a :href="`/tracking-local?numeroRastreo=${detalleEnvio.rastreo}`">{{  detalleEnvio.rastreo }}</a> </td>
                      <td><strong>Número de Pedido:</strong><br>{{ detalleEnvio.pedidos_concatenados }}</td>
                      <td><strong>Guía Remitente:</strong><br>{{ detalleEnvio.guias_concatenadas }}</td>
                    </tr>
                    <tr>
                      <td><strong>Número Factura:</strong><br>{{ detalleEnvio.facturas_concatenadas }}</td>
                      <td><strong>Orden de Compra:</strong><br>{{ detalleEnvio.oc_concatenadas }}</td>
                      <td><strong>Documentos Extras:</strong><br>{{ detalleEnvio.doc_extras_concatenadas }}</td>
                    </tr>
                    <tr>
                      <td colspan="1"><strong>Agencia:</strong><br>{{ detalleEnvio.agencia }}</td>
                      <td colspan="1"><strong>Clave:</strong><br>{{ detalleEnvio.clave_agencia }}</td>
                      <td><strong>Fecha y hora de llegada</strong>
                        <br> {{ formatFecha(detalleEnvio.fecha_entrega_agencia) }} - {{
                          detalleEnvio.rango_horario_agencia }}
                      </td>
                    </tr>
                    <tr>
                      <td colspan="3"><strong>Observaciones:</strong><br>{{ detalleEnvio.observacion_final }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Información de Envío -->
              <div class="mb-4">
                <h6 class="fw-bold text-primary">Información de Envío</h6>
                <table class="table table-bordered table-sm">
                  <tbody>
                    <tr>
                      <td><strong>RUC Remitente:</strong><br>{{ detalleEnvio.ruc_remitente }}</td>
                      <td><strong>Remitente (Cliente Inicial - Final):</strong><br>{{ detalleEnvio.empresa_sucursal }}
                      </td>
                      <td><strong>Dirección:</strong><br>{{ detalleEnvio.sucursal_remitente.direccion }} - {{
                        detalleEnvio.sucursal_remitente.distrito_nombre }} -
                        {{ detalleEnvio.sucursal_remitente.provincia_nombre }}</td>
                    </tr>
                    <tr>
                      <td><strong>RUC Destinatario:</strong><br>{{ detalleEnvio.ruc_destinatario }}</td>
                      <td><strong>Destinatario (Cliente Inicial - Final):</strong><br>{{
                        detalleEnvio.empresa_sucursal_destino }}</td>
                      <td><strong>Dirección:</strong><br>{{ detalleEnvio.sucursal_destinatario.direccion }} - {{
                        detalleEnvio.sucursal_destinatario.distrito_nombre }} -
                        {{ detalleEnvio.sucursal_destinatario.provincia_nombre }}</td>
                    </tr>
                    <tr>
                      <td><strong>Tipo de Unidad:</strong><br>{{ detalleEnvio.tipo_unidad }}</td>
                      <td><strong>Fecha de Salida:</strong><br>{{ formatFecha(detalleEnvio.fecha_salida) }}</td>
                      <td><strong>Fecha de Entrega:</strong><br>{{ formatFecha(detalleEnvio.fecha_entrega) }}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Horario de Salida:</strong><br>
                        {{ new Date('1970-01-01T' + detalleEnvio.hora_salida).toLocaleTimeString('es-PE', {
                          hour:
                            '2-digit', minute: '2-digit', hour12: true
                        }) }}
                      </td>
                      <td>
                        <strong>Horario de Entrega:</strong><br>
                        {{ new Date('1970-01-01T' + detalleEnvio.hora_llegada_maxima).toLocaleTimeString('es-PE', {
                          hour:
                            '2-digit', minute: '2-digit', hour12: true
                        }) }}
                      </td>

                      <td><strong>Guía de Transportista:</strong><br>{{ detalleEnvio.guias_transportistas_concatenadas }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>


            </div>
            <div class="d-block d-md-none">
              <!-- Datos Generales -->
              <h6 class="mt-4 fw-bold text-primary">Datos Generales</h6>
              <div class="card mb-2" v-if="detalleEnvio">
                <div class="card-body">
                  <p><strong>N° de Rastreo:</strong><a :href="`/tracking-local?numeroRastreo=${detalleEnvio.rastreo}`">{{  detalleEnvio.rastreo }}</a> </p>
                  <p><strong>Detalle: </strong>{{ detalleEnvio.detalles[0]?.detalle_metodo_envio }}</p>
                  <p><strong>Número de Pedido:</strong> {{ detalleEnvio.nro_pedido }}</p>
                  <p><strong>Guía Remitente:</strong> {{ detalleEnvio.guia_remitente }}</p>
                  <p><strong>Número Factura:</strong> {{ detalleEnvio.facturas_concatenadas }}</p>
                  <p><strong>Orden de Compra:</strong> {{ detalleEnvio.oc_concatenadas }}</p>
                  <p><strong>Documentos Extras:</strong> {{ detalleEnvio.documentos_extras }}</p>
                  <p><strong>Observaciones:</strong> {{ detalleEnvio.observaciones }}</p>
                  <p><strong>Agencia:</strong> {{ detalleEnvio.agencia }}</p>
                </div>
              </div>

              <!-- Información de Envío -->
              <h6 class=" mt-4 fw-bold text-primary">Información de Envío</h6>
              <div class="card mb-2">
                <div class="card-body">
                  <p><strong>RUC Remitente:</strong> {{ detalleEnvio.ruc_remitente }}</p>
                  <p><strong>Remitente:</strong> {{ detalleEnvio.empresa_sucursal }}</p>
                  <p><strong>Dirección Remitente:</strong>
                    {{ detalleEnvio.sucursal_remitente.direccion }} -
                    {{ detalleEnvio.sucursal_remitente.distrito_nombre }} -
                    {{ detalleEnvio.sucursal_remitente.provincia_nombre }}
                  </p>
                  <p><strong>RUC Destinatario:</strong> {{ detalleEnvio.ruc_destinatario }}</p>
                  <p><strong>Destinatario:</strong> {{ detalleEnvio.empresa_sucursal_destino }}</p>
                  <p><strong>Dirección Destinatario:</strong>
                    {{ detalleEnvio.sucursal_destinatario.direccion }} -
                    {{ detalleEnvio.sucursal_destinatario.distrito_nombre }} -
                    {{ detalleEnvio.sucursal_destinatario.provincia_nombre }}
                  </p>
                  <p><strong>Tipo de Unidad:</strong> {{ detalleEnvio.tipo_unidad }}</p>
                  <p><strong>Fecha de Salida:</strong> {{ formatFecha(detalleEnvio.fecha_salida) }}</p>
                  <p><strong>Fecha de Entrega:</strong> {{ formatFecha(detalleEnvio.fecha_entrega) }}</p>
                  <p><strong>Horario de Salida:</strong>
                    {{ new Date('1970-01-01T' + detalleEnvio.hora_salida).toLocaleTimeString('es-PE', {
                      hour: '2-digit', minute: '2-digit', hour12: true
                    }) }}
                  </p>
                  <p><strong>Horario de Entrega:</strong>
                    {{ new Date('1970-01-01T' + detalleEnvio.hora_llegada_maxima).toLocaleTimeString('es-PE', {
                      hour: '2-digit', minute: '2-digit', hour12: true
                    }) }}
                  </p>
                </div>
              </div>


            </div>
          </div>


        </div>
      </div>

    </div>

    <div v-if="modalEstados" class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="uploadModalLabel">Gestión de Estados</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" @click="modalEstados = false"
              aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <!-- Una sola grilla: los datos que van juntos comparten fila (evita scroll dentro del modal) -->
            <div class="row g-3">
              <div class="col-12">
                <label for="nuevoEstado">Estado del Envío</label>
                <select id="nuevoEstado" class="form-control" v-model="nuevoEstado">
                  <option v-for="item in estadosEnvio" :value="item.id" :key="item.id">{{ item.nombre }}</option>
                </select>
              </div>
              <div class="col-12" v-if="nuevoEstado == 10">
                <label for="conformidadDraeger">Conformidad de Draeger (N°hojas)</label>
                <select v-model="conformidad_draeger" class="form-control" id="conformidadDraeger">
                  <option value="SIN RECEPCIONAR">SIN RECEPCIONAR</option>
                  <option value="RECEPCIONADO">RECEPCIONADO</option>
                  <option value="OBSERVADO">OBSERVADO</option>
                </select>
              </div>

              <template v-if="nuevoEstado == 5 || nuevoEstado == 12">
                <div class="col-md-6">
                  <label class="form-label">Cantidad de Bultos en Origen</label>
                  <input type="number" v-model="cantidad_bultos_origen" class="form-control" />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Estado de los Bultos en Origen</label>
                  <select v-model="estado_bultos_origen" class="form-control">
                    <option disabled value="">Seleccione un estado</option>
                    <option>SIN DAÑO</option>
                    <option>CON DAÑO</option>
                  </select>
                </div>
              </template>

              <!-- Campos para estado 7: Entrega -->
              <template v-if="nuevoEstado == 7">
                <div class="col-md-6">
                  <label class="form-label">Cantidad de Bultos en Destino</label>
                  <input type="number" v-model="cantidad_bultos_destino" class="form-control" />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Estado de los Bultos en Destino</label>
                  <select v-model="estado_bultos_destino" class="form-control">
                    <option disabled value="">Seleccione un estado</option>
                    <option>SIN DAÑO</option>
                    <option>CON DAÑO</option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Conformidad de la entrega</label>
                  <select v-model="conformidad_entrega" class="form-control">
                    <option disabled value="">Seleccione un estado</option>
                    <option>ACEPTADO</option>
                    <option>RECHAZADO</option>
                    <option>EN CUSTODIA</option>
                    <option>CANCELADO</option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label for="clave_agencia">Clave de Agencia</label>
                  <input type="text" id="clave_agencia" class="form-control" v-model="nuevoArchivo.clave_agencia">
                </div>
                <div class="col-md-6">
                  <label for="fecha_entrega_agencia">Fecha de entrega en Agencia</label>
                  <input type="date" id="fecha_entrega_agencia" class="form-control" v-model="nuevoArchivo.fecha_entrega_agencia">
                </div>
                <div class="col-md-6">
                  <label for="rango_horario_agencia">Horario de entrega en Agencia</label>
                  <input type="time" v-model="nuevoArchivo.rango_horario_agencia" class="form-control" name="rango_horario_agencia"
                    id="rango_horario_agencia">
                </div>
              </template>

              <template v-if="nuevoEstado != 10">
                <div class="col-md-6">
                  <label for="tipoArchivo">Tipo de Archivo</label>
                  <select v-model="tipoArchivo" class="form-control" id="tipoArchivo">
                    <option value="">-- Selecciona un tipo --</option>
                    <option value="fotos">📸 Fotos (JPG / PNG)</option>
                    <option value="videos">🎥 Videos (MP4 / MOV / AVI)</option>
                    <option value="pdf">📄 PDF (escaneo o foto en PDF)</option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label for="archivo">¿Qué archivo?</label>
                  <select v-model="nuevoArchivo.tipo" class="form-control" id="archivo">
                    <option value="">-- Selecciona un archivo --</option>
                    <option v-for="(opcion, index) in opcionesFiltradas" :key="index" :value="opcion">
                      {{ opcion }}
                    </option>
                  </select>
                </div>
                <div class="col-12">
                  <label class="form-label">Comentario</label>
                  <textarea v-model="nuevoArchivo.comentario" class="form-control" rows="2"></textarea>
                </div>
              </template>
            </div>

            <!-- Drag & Drop Zone -->
            <div class="dropzone mt-3" v-if="nuevoEstado != 8" @dragover.prevent="dragging = true"
              @dragleave.prevent="dragging = false" @drop.prevent="soltarArchivo" @click="abrirExplorador">
              <div v-if="comprimiendoArchivo" class="dropzone-content">
                <div class="spinner-border text-primary mb-2" role="status"></div>
                <p class="mb-0">Optimizando archivo...</p>
              </div>
              <div v-else-if="!nuevoArchivo.nombre" class="dropzone-content">
                <i class="fas fa-cloud-upload-alt fa-3x"></i>
                <p>Arrastra un archivo aquí o haz clic para seleccionar</p>
                <input type="file" class="d-none" ref="fileInput" accept="application/pdf,image/*,video/*"
                  @change="seleccionarArchivo" />
              </div>
              <div v-else class="dropzone-preview">
                <img v-if="esImagen(nuevoArchivo.nombre)" :src="nuevoArchivo.url" class="img-thumbnail" width="100" />
                <p class="mb-1">{{ nuevoArchivo.nombre }}</p>
                <p v-if="pesoArchivo" class="text-muted small mb-0">{{ pesoArchivo }}</p>
              </div>
            </div>
            <div v-if="nuevoEstado == 8">
              <p class="p-3 text-danger">AHORA SUBE TU CARGO ESCANEADO POR GESTIÓN DE GUIAS</p>
            </div>

            <div v-if="subiendoArchivoEstado" class="mt-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="small text-muted">
                  {{ intentoSubidaGuia > 1 ? `Reintentando (intento ${intentoSubidaGuia} de ${maxIntentosSubida})...` : 'Subiendo archivo...' }}
                </span>
                <span class="small fw-semibold">{{ progresoSubidaEstado }}%</span>
              </div>
              <div class="progress" style="height:8px;">
                <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar"
                  :style="{ width: progresoSubidaEstado + '%' }"></div>
              </div>
              <p class="small text-muted mt-2 mb-0">
                {{ progresoSubidaEstado >= 100
                  ? 'Archivo enviado. Esperando confirmación del servidor...'
                  : 'No cierres esta ventana ni bloquees la pantalla hasta que termine.' }}
              </p>
            </div>

            <br>
            <button @click="gestionEstado()" :disabled="loadingActualizarEstado || comprimiendoArchivo"
              class="btn btn-primary btn-sm float-end">
              <span v-if="loadingActualizarEstado" class="spinner-border spinner-border-sm me-1" role="status"></span>
              {{ loadingActualizarEstado ? 'Actualizando...' : 'Actualizar Estado' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para subir archivos -->
    <div v-if="showModalArchivos" class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="uploadModalLabel">Documentos</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" @click="showModalArchivos = false"
              aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <!-- Tabla con los documentos agregados -->
            <div class="mt-4">
              <table class="table table-font-small">
                <thead>
                  <tr>
                    <th>Tipo de Documento</th>
                    <th>Archivo</th>
                    <th>Comentario</th>
                    <th class="col-acciones">Acciones</th>
                  </tr>
                </thead>
                <tbody class="table-border-bottom-0 padding-custom">
                  <tr v-for="(archivo, index) in archivos" :key="index">
                    <td v-if="archivo.nombre">{{ archivo.tipo }}</td>
                    <td v-if="archivo.nombre">
                      <span v-if="esImagen(archivo.nombre)">
                        <img :src="archivo.url" class="img-thumbnail" width="50" />
                      </span>
                      {{ archivo.nombre }}
                    </td>
                    <td v-if="archivo.nombre">{{ archivo.comentario }}</td>
                    <td v-if="archivo.nombre" class="col-acciones">
                      <button class="btn-icon btn-icon-view" @click="verArchivo(archivo)" title="Ver archivo" aria-label="Ver archivo"><i class="bi bi-eye"></i></button>
                      <button v-if="esSuperAdmin()" class="btn-icon btn-icon-del" @click="eliminarArchivo(archivo.id)" title="Eliminar archivo" aria-label="Eliminar archivo"><i class="bi bi-trash"></i></button>
                    </td>
                  </tr>
                  <tr v-if="archivos.length === 0" class="empty-row"><td colspan="4">No hay archivos subidos</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Gestión de Guías -->
    <div v-if="showModalGestionGuias" class="modal fade show d-block" tabindex="-1"
      style="background: rgba(0,0,0,0.5);">
      <!-- xl: la tabla tiene 8 columnas; scrollable para envíos con muchas guías -->
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="bi bi-file-earmark-text me-2"></i>Gestión de Guías</h5>
            <button type="button" class="btn-close" @click="showModalGestionGuias = false"></button>
          </div>
          <div class="modal-body">
            <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
              <span class="text-muted small">Envío</span>
              <span class="badge bg-primary fs-6 fw-semibold">{{ envioSeleccionadoGuias?.rastreo }}</span>
              <span v-if="envioSeleccionadoGuias?.detalles?.length" class="badge bg-light text-dark border">
                {{ envioSeleccionadoGuias.detalles.length }} {{ envioSeleccionadoGuias.detalles.length === 1 ? 'guía' : 'guías' }}
              </span>
            </div>
            <div
              v-if="envioSeleccionadoGuias && envioSeleccionadoGuias.detalles && envioSeleccionadoGuias.detalles.length">

              <!-- Tabla desktop -->
              <div class="d-none d-md-block table-responsive">
                <table class="table table-bordered table-sm table-hover align-middle">
                  <thead class="table-primary text-center" style="font-size:0.82rem;">
                    <tr>
                      <th>Guía Remitente</th>
                      <th>Guía Transportista</th>
                      <th>N° Pedido</th>
                      <th>Orden Compra</th>
                      <th>Factura</th>
                      <th>Docs. Extras</th>
                      <th>Método Envío</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody style="font-size:0.85rem;">
                    <tr v-for="detalle in envioSeleccionadoGuias.detalles" :key="detalle.id">
                      <td>{{ detalle.guia_remitente || '-' }}</td>
                      <td>{{ detalle.guia_transportista || '-' }}</td>
                      <td>{{ detalle.nro_pedido || '-' }}</td>
                      <td>{{ detalle.orden_compra || '-' }}</td>
                      <td>{{ detalle.numero_factura || '-' }}</td>
                      <td>{{ detalle.documentos_extras || '-' }}</td>
                      <td>{{ detalle.detalle_metodo_envio || '-' }}</td>
                      <td class="text-center" style="white-space:nowrap; width:90px;">
                        <button v-if="detalle.archivo_url" class="btn btn-sm btn-info me-1"
                          @click="verArchivoGuia(detalle.archivo_url)" title="Ver archivo">
                          <i class="bi bi-eye"></i>
                        </button>
                        <button v-if="esSuperAdminOInterno()" class="btn btn-sm btn-primary"
                          @click="subirArchivoGuia(detalle)" title="Subir archivo">
                          <i class="bi bi-upload"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Cards móvil -->
              <div class="d-block d-md-none">
                <div v-for="(detalle, i) in envioSeleccionadoGuias.detalles" :key="detalle.id"
                  class="card mb-3 border" style="border-color:#c5cae9!important; border-radius:10px; overflow:hidden;">
                  <div class="card-header py-2 d-flex justify-content-between align-items-center"
                    style="background:#3f51b5!important; color:#fff!important; border-bottom:none;">
                    <span class="fw-semibold small">Guía #{{ i + 1 }}</span>
                    <span v-if="detalle.archivo_url" class="badge fw-normal px-2 py-1"
                      style="background:rgba(255,255,255,0.25)!important; color:#fff!important; font-size:0.72rem;">
                      <i class="bi bi-check-circle me-1"></i>Con archivo
                    </span>
                  </div>
                  <div class="card-body py-3 px-3" style="background:#f8f9ff;">
                    <div class="row g-2" style="font-size:0.85rem;">
                      <div class="col-6">
                        <div class="text-muted" style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.4px;">Guía Remitente</div>
                        <div class="fw-semibold text-dark">{{ detalle.guia_remitente || '—' }}</div>
                      </div>
                      <div class="col-6">
                        <div class="text-muted" style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.4px;">Guía Transportista</div>
                        <div class="fw-semibold text-dark">{{ detalle.guia_transportista || '—' }}</div>
                      </div>
                      <div class="col-6">
                        <div class="text-muted" style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.4px;">N° Pedido</div>
                        <div class="fw-semibold text-dark">{{ detalle.nro_pedido || '—' }}</div>
                      </div>
                      <div class="col-6">
                        <div class="text-muted" style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.4px;">Orden Compra</div>
                        <div class="fw-semibold text-dark">{{ detalle.orden_compra || '—' }}</div>
                      </div>
                      <div class="col-6">
                        <div class="text-muted" style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.4px;">Factura</div>
                        <div class="fw-semibold text-dark">{{ detalle.numero_factura || '—' }}</div>
                      </div>
                      <div class="col-6">
                        <div class="text-muted" style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.4px;">Método Envío</div>
                        <div class="fw-semibold text-dark">{{ detalle.detalle_metodo_envio || '—' }}</div>
                      </div>
                      <div class="col-12" v-if="detalle.documentos_extras">
                        <div class="text-muted" style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.4px;">Docs. Extras</div>
                        <div class="fw-semibold text-dark">{{ detalle.documentos_extras }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="card-footer py-2 d-flex gap-2"
                    style="background:#eef0fb!important; border-top:1px solid #c5cae9!important; color:inherit!important; font-weight:normal!important; padding:8px 12px!important;">
                    <button v-if="detalle.archivo_url" class="btn btn-sm flex-fill"
                      style="background:#0097a7; color:#fff; border:none;"
                      @click="verArchivoGuia(detalle.archivo_url)">
                      <i class="bi bi-eye me-1"></i>Ver archivo
                    </button>
                    <button v-if="esSuperAdminOInterno()" class="btn btn-sm flex-fill"
                      style="background:#3f51b5; color:#fff; border:none;"
                      @click="subirArchivoGuia(detalle)">
                      <i class="bi bi-upload me-1"></i>Subir archivo
                    </button>
                  </div>
                </div>
              </div>

            </div>
            <div v-else class="text-muted text-center py-3">No hay detalles de guías para este envío.</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para Subir Archivos de Guía -->
    <div v-if="showModalArchivoGuia" class="modal fade show d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="bi bi-cloud-arrow-up me-2"></i>Subir archivo del documento</h5>
            <button type="button" class="btn-close" @click="cerrarModalArchivoGuia"></button>
          </div>
          <div class="modal-body">
            <!-- Datos del documento: solo los que tiene -->
            <div v-if="detalleGuiaSeleccionado && referenciasGuia(detalleGuiaSeleccionado).length" class="guia-refs">
              <div v-for="ref in referenciasGuia(detalleGuiaSeleccionado)" :key="ref.label" class="guia-ref">
                <span class="guia-ref-label">{{ ref.label }}</span>
                <span class="guia-ref-valor">{{ ref.valor }}</span>
              </div>
            </div>
            <!-- El tipo de archivo es fijo: se muestra como dato, no como select bloqueado -->
            <div class="guia-tipo">
              <i class="bi bi-file-earmark-pdf"></i>
              <div>
                <div class="guia-tipo-nombre">{{ archivoGuia.tipo }}</div>
                <div class="guia-tipo-detalle">PDF (escaneo o foto en PDF)</div>
              </div>
            </div>

            <div class="row mt-3">
              <div class="col-md-12">
                <label class="form-label">Comentario <span class="guia-opcional">(opcional)</span></label>
                <textarea v-model="archivoGuia.comentario" class="form-control" rows="2"
                  placeholder="Algún detalle sobre el archivo"></textarea>
              </div>
            </div>

            <!-- Drag & Drop Zone para Guía -->
            <div class="dropzone mt-3" :class="{ 'drop-hover': draggingGuia }" @dragover.prevent="draggingGuia = true"
              @dragleave.prevent="draggingGuia = false" @drop.prevent="soltarArchivoGuia" @click="abrirExploradorGuia">
              <div v-if="comprimiendoArchivoGuia" class="dropzone-content">
                <div class="spinner-border text-primary mb-2" role="status"></div>
                <p class="mb-0">Optimizando archivo...</p>
              </div>
              <div v-else-if="!archivoGuia.nombre" class="dropzone-content">
                <i class="fas fa-cloud-upload-alt fa-3x"></i>
                <p>Arrastra un archivo aquí o haz clic para seleccionar</p>
                <input type="file" class="d-none" ref="fileInputGuia" accept="application/pdf,image/*"
                  @change="seleccionarArchivoGuia" />
              </div>
              <div v-else class="dropzone-preview">
                <img v-if="esImagen(archivoGuia.nombre)" :src="archivoGuia.url" class="img-thumbnail" width="100" />
                <p class="mb-1"><strong>{{ archivoGuia.nombre }}</strong></p>
                <p v-if="pesoArchivoGuia" class="text-muted small mb-0">{{ pesoArchivoGuia }}</p>
                <button class="btn btn-sm btn-outline-secondary mt-2" @click.stop="limpiarArchivoGuia"
                  :disabled="loadingSubirArchivoGuia">
                  <i class="bi bi-x-circle"></i> Cambiar archivo
                </button>
              </div>
            </div>

            <div v-if="loadingSubirArchivoGuia" class="mt-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="small text-muted">
                  {{ intentoSubidaGuia > 1 ? `Reintentando (intento ${intentoSubidaGuia} de ${maxIntentosSubida})...` : 'Subiendo archivo...' }}
                </span>
                <span class="small fw-semibold">{{ progresoSubidaGuia }}%</span>
              </div>
              <div class="progress" style="height:8px;">
                <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar"
                  :style="{ width: progresoSubidaGuia + '%' }"></div>
              </div>
              <p class="small text-muted mt-2 mb-0">
                {{ progresoSubidaGuia >= 100
                  ? 'Archivo enviado. Esperando confirmación del servidor...'
                  : 'No cierres esta ventana ni bloquees la pantalla hasta que termine.' }}
              </p>
            </div>

            <p v-if="!archivoGuia.archivo" class="guia-hint">Selecciona el archivo para poder subirlo.</p>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="cerrarModalArchivoGuia" :disabled="loadingSubirArchivoGuia">Cancelar</button>
            <button class="btn btn-primary" @click="guardarArchivoGuia" :disabled="!archivoGuia.archivo || loadingSubirArchivoGuia || comprimiendoArchivoGuia">
              <span v-if="loadingSubirArchivoGuia" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <i v-else class="bi bi-upload me-1"></i>
              {{ loadingSubirArchivoGuia ? 'Subiendo...' : 'Subir archivo' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'
import { validarRangoFechas, LIMITE_EXPORTACION, mensajeErrorDescarga } from '@/utils/rangoFechas'
import moment from 'moment'
import ShippingANWLocal from '@/components/ShippingANWLocal.vue'
import PaginacionListado from '@/components/PaginacionListado.vue'

export default {
  name: 'ShippingLocalListPage',
  components: {
    ShippingANWLocal,
    PaginacionListado
  },
  data() {
    return {
      isLoading: true,
      exportando: false,
      loadingSubirArchivoGuia: false,
      loadingActualizarEstado: false,
      user: {},
      tipoEnvio: this.$route.params.tipo_envio,
      modalDetalle: false,
      showModalArchivos: false,
      modalGestionANW: false,
      estadosEnvio: [],
      modalEstados: false,
      cantidad_bultos_origen: '',
      cantidad_bultos_destino: '',
      estado_bultos_origen: '',
      estado_bultos_destino: '',
      conformidad_entrega: '',
      nuevoEstado: '',
      envioId: null,
      tipoArchivo: '',
      archivoSeleccionado: '',
      opciones: {
        fotos: [
          'Foto de mercadería en origen',
          'Foto del punto de recojo (opcional, para incidentes o validación)',
          'Foto de mercadería en el cliente',
          'Foto de bultos dañados (si aplica)',
          'Foto del cargo entregado en oficina (DRAEGER)',
          'Foto de acceso bloqueado o incidente en ruta',
          'Foto del tráfico o desvíos durante tránsito',
          'Foto de rechazo (cuando el cliente no acepta la entrega)',
          'Otros'
        ],
        videos: [
          'Video de la mercadería en origen (muestra condiciones)',
          'Video durante la ruta (incidente, tráfico o desvío)',
          'Video del estado de la mercadería al llegar a destino',
          'Otros'
        ],
        pdf: [
          'Cargo firmado por el cliente (en destino)',
          'Formato de recojo (si aplica)',
          'Cargo de entrega en agencia',
          'Acta de entrega (si aplica)',
          'Formato de devolución o rechazo firmado',
          'Documento adicional solicitado por Draeger'
        ]
      },
      filtros: {
        tipo_paquete: "mercaderia",
        ruc: '',
        razon_social: '',
        cliente_final: '',
        pedido: '',
        guia: '',
        serie_guia: '',
        fecha_inicio: '',
        fecha_fin: '',
        id_vehiculo: '',
        id_modalidades_envio: '',
        estado_entrega: '',
        modalidad_envio: ''
      },
      vehiculos: [],
      comentario: '',
      detalleEnvio: {},
      envios: [],
      pagina: 1,
      porPagina: 25,
      totalPaginas: 1,
      totalEnvios: 0,
      empresas: [
        { id: 1, nombre: 'Empresa A' },
        { id: 2, nombre: 'Empresa B' },
      ],
      tipoUnidades: [
        { id: 3, tipo_unidad: 'Camión 10T' },
        { id: 4, tipo_unidad: 'Camión 20T' },
      ],
      archivos: [],
      nuevoArchivo: {
        tipo: '',
        archivo: null,
        nombre: '',
        comentario: '',
        url: null,
        rango_horario_agencia: '',
        clave_agencia: '',
        fecha_entrega_agencia: '',
      },
      dragging: false,
      envioSeleccionadoGuias: null,
      showModalGestionGuias: false,
      showModalArchivoGuia: false,
      detalleGuiaSeleccionado: null,
      archivoGuia: {
        tipo: '',
        archivo: null,
        nombre: '',
        comentario: '',
        url: null,
      },
      tipoArchivoGuia: '',
      draggingGuia: false,
      progresoSubidaGuia: 0,
      pesoArchivoGuia: '',
      comprimiendoArchivoGuia: false,
      intentoSubidaGuia: 0,
      maxIntentosSubida: 2,
      subiendoArchivoEstado: false,
      progresoSubidaEstado: 0,
      pesoArchivo: '',
      comprimiendoArchivo: false,
    }
  },
  watch: {
    '$route.path'() {
      this.limpiarFiltros()
      this.buscarEnvios()
    },
  },
  computed: {
    rutaEnvio() {
      return 'envios-locales-emergencias';
    },
    opcionesFiltradas() {
      return this.tipoArchivo ? this.opciones[this.tipoArchivo] : [];
    },
    opcionesFiltradasGuia() {
      return this.tipoArchivoGuia ? this.opciones[this.tipoArchivoGuia] : [];
    },
  },
  mounted() {
    this.getEstadosEnvio()
    this.buscarEnvios()
    this.getVehiculos()
    this.user = JSON.parse(localStorage.getItem('user'))
  },
  methods: {

    esAdmin() {
      const cargo = this.user?.cargo?.toLowerCase()
      return cargo === 'admin' || cargo === 'admin-draeger'
    },

    esSuperAdmin() {
      return this.user?.cargo?.toLowerCase() === 'admin'
    },
    esSuperAdminOInterno() {
      console.log("this.user", this.user)
      return this.user?.cargo?.toLowerCase() === 'admin' || this.user?.tipo_usuario == 'interno'
    },

    formatHoraAMPM(hora) {
      return moment(hora, 'HH:mm:ss').format('hh:mm A');
    },
    async getVehiculos() {
      const response = await axios.get('/vehiculos-local')
      this.vehiculos = response.data;
    },
    getTipoEnvioTexto() {
      switch (this.tipoEnvio) {
        case 'exclusivos':
          return 'Exclusivos'
        case 'consolidados':
          return 'Consolidados'
        case 'aereos':
          return 'Aéreos'
        default:
          return 'Desconocido'
      }
    },
    async getEstadosEnvio() {
      this.isLoading = true;
      const { data } = await axios.get('estados-envio-local')
      this.estadosEnvio = data
      console.log("this.user", this.user)
      if (this.user.cargo != "admin-draeger" && this.user.cargo != "admin" && this.user.tipo_usuario != "interno") {
        this.estadosEnvio = this.estadosEnvio.filter(item => [15, 10].includes(item.id));
      }
      this.isLoading = false;
    },
    formatFecha(fecha) {
      if (!fecha) return ''
      const [year, month, day] = fecha.split('-')
      return new Date(year, month - 1, day).toLocaleDateString('es-PE', {
        dateStyle: 'short',
      })
    },
    editarEnvio(envio) {
      this.$router.push(`/editar-envio-local/${envio.id_envio_local}`)
    },
    async guardarGestion() {
      try {
        const resultado = await this.$refs.shippingANWRef.submitDesdePadre();
        this.buscarEnvios();
        console.log('Resultado desde el hijo:', resultado);
      } catch (error) {
        console.error('Error al guardar desde el hijo:', error);
      }
    },
    async exportarExcel() {
      const errorRango = validarRangoFechas(this.filtros.fecha_inicio, this.filtros.fecha_fin, LIMITE_EXPORTACION);
      if (errorRango) {
        Swal.fire({ icon: 'info', title: 'Ajusta el rango de fechas', text: errorRango });
        return;
      }
      if (this.exportando) return; // ya hay una exportación en curso
      this.exportando = true;
      try {
        const response = await axios.get('/exportar-envios-local', {
          params: this.filtros,
          responseType: 'blob'
        });

        // Generar nombre dinámico: provincia_mercaderia_YYYYmmddHHMMss.xlsx
        const now = new Date();
        const pad = (n) => n.toString().padStart(2, '0');
        const fileName = `local_mercaderia_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.xlsx`;

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error al exportar:', error);
        Swal.fire({
          icon: 'error',
          title: 'No se pudo exportar',
          text: await mensajeErrorDescarga(error, 'Ocurrió un error al generar el archivo. Intenta nuevamente.'),
        });
      } finally {
        this.exportando = false;
      }
    },

    openEstadoEnvio(item) {
      this.detalleEnvio = { ...item }
      this.nuevoEstado = this.detalleEnvio.estado_entrega
      this.cantidad_bultos_origen = this.detalleEnvio.cantidad_bultos_origen
      this.estado_bultos_origen = this.detalleEnvio.estado_bultos_origen
      this.cantidad_bultos_destino = this.detalleEnvio.cantidad_bultos_destino
      this.estado_bultos_destino = this.detalleEnvio.estado_bultos_destino,
        this.conformidad_entrega = this.detalleEnvio.conformidad_entrega,

        // Cargar campos de agencia si existen
        this.nuevoArchivo.clave_agencia = this.detalleEnvio.clave_agencia || '';
      this.nuevoArchivo.fecha_entrega_agencia = this.detalleEnvio.fecha_entrega_agencia || '';
      this.nuevoArchivo.rango_horario_agencia = this.detalleEnvio.rango_horario_agencia || '';

      this.modalEstados = true
    },
    async gestionEstado() {
      this.loadingActualizarEstado = true;
      console.log("nuevoEstado", this.nuevoEstado);

      let data = {
        id_envio_local: this.detalleEnvio.id_envio_local,
        estado_entrega: this.nuevoEstado,
      };

      // Validaciones según el estado
      if (this.nuevoEstado == 5 || this.nuevoEstado == 12) {
        if (!this.cantidad_bultos_origen || !this.estado_bultos_origen) {
          Swal.fire({
            icon: 'warning',
            title: 'Campos requeridos',
            text: 'Debes ingresar la cantidad y estado de los bultos en origen.',
          });
          this.loadingActualizarEstado = false;
          return;
        }

        data.cantidad_bultos_origen = this.cantidad_bultos_origen;
        data.estado_bultos_origen = this.estado_bultos_origen;
      }

      if (this.nuevoEstado == 7) {
        if (!this.cantidad_bultos_destino || !this.estado_bultos_destino) {
          Swal.fire({
            icon: 'warning',
            title: 'Campos requeridos',
            text: 'Debes ingresar la cantidad y estado de los bultos en destino.',
          });
          this.loadingActualizarEstado = false;
          return;
        }

        data.cantidad_bultos_destino = this.cantidad_bultos_destino;
        data.estado_bultos_destino = this.estado_bultos_destino;
      }
      if (this.nuevoEstado == 10) {
        data.conformidad_draeger = this.conformidad_draeger;
      }

      if (this.nuevoEstado == 7) {
        if (!this.conformidad_entrega) {
          Swal.fire({
            icon: 'warning',
            title: 'Campos requeridos',
            text: 'Debes seleccionar la conformidad de la entrega.',
          });
          this.loadingActualizarEstado = false;
          return;
        }
        data.conformidad_entrega = this.conformidad_entrega;

        // Agregar los campos de agencia
        if (this.nuevoArchivo.clave_agencia) {
          data.clave_agencia = this.nuevoArchivo.clave_agencia;
        }
        if (this.nuevoArchivo.fecha_entrega_agencia) {
          data.fecha_entrega_agencia = this.nuevoArchivo.fecha_entrega_agencia;
        }
        if (this.nuevoArchivo.rango_horario_agencia) {
          data.rango_horario_agencia = this.nuevoArchivo.rango_horario_agencia;
        }
      }


      // Evita que el celular suspenda la subida al apagarse la pantalla.
      const wakeLock = await this.mantenerPantallaActiva();

      try {
        await axios.post(`estado-envio-local`, data);
        await this.agregarArchivo();
        this.cargarArchivos(this.detalleEnvio.id_envio_local);
        this.buscarEnvios();
        Swal.fire({
          icon: 'success',
          title: 'Estado Actualizado',
          text: 'El estado se ha actualizado correctamente.',
        });

        // Limpiar el formulario del modal
        this.limpiarFormularioModal();
        this.modalEstados = false;
      } catch (error) {
        console.error(`Error al actualizar el estado del envío`, error);
        Swal.fire({
          icon: 'error',
          title: 'No se pudo actualizar el estado',
          text: error.response?.data?.message || 'Revisa tu conexión e inténtalo nuevamente.',
        });
      } finally {
        // Antes no se reseteaba aqui: tras un error el boton quedaba
        // desactivado hasta refrescar la pagina.
        this.loadingActualizarEstado = false;
        if (wakeLock) {
          wakeLock.release().catch(() => {});
        }
      }
    },
    limpiarFormularioModal() {
      // Reiniciar los campos del formulario
      this.nuevoEstado = '';
      this.tipoArchivo = '';
      this.cantidad_bultos_origen = '';
      this.cantidad_bultos_destino = '';
      this.estado_bultos_origen = '';
      this.estado_bultos_destino = '';
      this.conformidad_entrega = '';
      if (this.nuevoArchivo.url) {
        URL.revokeObjectURL(this.nuevoArchivo.url);
      }
      this.nuevoArchivo = {
        tipo: '',
        archivo: null,
        nombre: '',
        comentario: '',
        url: null,
        rango_horario_agencia: '',
        clave_agencia: '',
        fecha_entrega_agencia: '',
      };
      this.pesoArchivo = '';

      // Limpiar el input file si existe
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    async agregarArchivo() {
      // El comentario viaja unicamente en esta peticion (el backend lo guarda
      // aunque no haya archivo, como un registro mas del historial). Por eso
      // solo se omite el envio si no hay ni archivo ni comentario.
      const tieneArchivo = !!this.nuevoArchivo.archivo
      const tieneComentario = !!(this.nuevoArchivo.comentario && this.nuevoArchivo.comentario.trim())

      if (!tieneArchivo && !tieneComentario) return

      const formData = new FormData()
      formData.append('envio_id', this.detalleEnvio.id_envio_local)
      formData.append('tipo', this.nuevoArchivo.tipo)
      if (tieneArchivo) {
        formData.append('archivo', this.nuevoArchivo.archivo)
      }
      formData.append('comentario', this.nuevoArchivo.comentario)
      formData.append('id_estados_envio_local', this.nuevoEstado)

      this.subiendoArchivoEstado = true
      this.progresoSubidaEstado = 0

      try {
        const data = await this.postConReintentos(formData, {
          onProgreso: (porcentaje) => {
            this.progresoSubidaEstado = porcentaje
          },
          onReintento: (intento) => {
            this.intentoSubidaGuia = intento
          },
        })

        this.archivos.push(data)
        if (this.nuevoArchivo.url) {
          URL.revokeObjectURL(this.nuevoArchivo.url)
        }
        this.nuevoArchivo = {
          tipo: '',
          archivo: null,
          nombre: '',
          comentario: '',
          url: null,
        }
        this.pesoArchivo = ''
      } catch (error) {
        console.error('Error al subir archivo', error)

        let mensaje = 'No se pudo subir el archivo. Inténtelo de nuevo.'
        if (error.code === 'ECONNABORTED') {
          mensaje = 'La subida tardó demasiado. El estado sí se actualizó, pero el archivo no se subió.'
        } else if (error.response && error.response.status === 413) {
          mensaje = 'El archivo es demasiado pesado para el servidor.'
        }

        Swal.fire({
          icon: 'error',
          title: 'No se pudo subir el archivo',
          text: mensaje,
        })
      } finally {
        this.subiendoArchivoEstado = false
        this.progresoSubidaEstado = 0
        this.intentoSubidaGuia = 0
      }
    },
    async eliminarEnvio(envio) {
      const confirmacion = await Swal.fire({
        title: '¿Estás seguro?',
        text: `Vas a eliminar el envío ${envio.rastreo || ''}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
      });

      if (confirmacion.isConfirmed) {
        try {
          // Aquí llamás a tu endpoint para eliminar
          await axios.delete(`/envios-locales/${envio.id_envio_local}`);

          // Mensaje de éxito
          await Swal.fire({
            icon: 'success',
            title: 'Eliminado',
            text: 'El envío fue eliminado correctamente.',
            timer: 1500,
            showConfirmButton: false,
          });

          // Recargar lista (si lo necesitas)
          this.buscarEnvios(); // o como se llame tu método para recargar

        } catch (error) {
          console.error(error);

          // Obtener el mensaje de error del servidor
          let errorMessage = 'Hubo un problema al eliminar el envío.';
          if (error.response && error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          }

          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: errorMessage,
          });
        }
      }
    },
    mostrarDetalle(item) {
      this.modalDetalle = true
      this.detalleEnvio = { ...item }
    },
    cerrarDetalle() {
      this.modalDetalle = false
    },
    abrirGestionarModal(item) {
      this.detalleEnvio = item;
      console.log("detalleEnvio", this.detalleEnvio)
      this.modalGestionANW = true
    },
    abrirGestionGuiasModal(envio) {
      this.envioSeleccionadoGuias = envio;
      this.showModalGestionGuias = true;
    },
    // Datos del documento para el modal de subida (solo los que tienen valor)
    referenciasGuia(detalle) {
      return [
        { label: 'Guía remitente', valor: detalle.guia_remitente },
        { label: 'Guía transportista', valor: detalle.guia_transportista },
        { label: 'N° pedido', valor: detalle.nro_pedido },
        { label: 'Orden de compra', valor: detalle.orden_compra },
        { label: 'Factura', valor: detalle.numero_factura },
      ].filter(ref => ref.valor)
    },
    subirArchivoGuia(detalle) {
      Swal.fire({
        title: '¿Subir archivo de guía?',
        text: 'Solo puedes subir si ya actualizaste el estado a CARGO PDF SUBIDO.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, continuar',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          this.detalleGuiaSeleccionado = detalle;
          this.limpiarArchivoGuia();
          this.loadingSubirArchivoGuia = false;
          this.showModalArchivoGuia = true;
        }
      });
    },
    cerrarModalArchivoGuia() {
      this.showModalArchivoGuia = false;
      this.detalleGuiaSeleccionado = null;
      this.limpiarArchivoGuia();
      this.loadingSubirArchivoGuia = false;
    },
    limpiarArchivoGuia() {
      if (this.archivoGuia.url) {
        URL.revokeObjectURL(this.archivoGuia.url);
      }
      this.archivoGuia = {
        tipo: 'Cargo firmado por el cliente (en destino)',
        archivo: null,
        nombre: '',
        comentario: '',
        url: null,
      };
      this.tipoArchivoGuia = 'pdf';
      this.draggingGuia = false;
      this.progresoSubidaGuia = 0;
      this.pesoArchivoGuia = '';
    },
    seleccionarArchivoGuia(event) {
      this.procesarArchivoGuia(event.target.files[0]);
      event.target.value = '';
    },
    abrirExploradorGuia() {
      if (this.loadingSubirArchivoGuia || this.comprimiendoArchivoGuia) return;
      // El input solo existe mientras no haya archivo elegido.
      if (this.$refs.fileInputGuia) {
        this.$refs.fileInputGuia.click();
      }
    },
    soltarArchivoGuia(event) {
      this.draggingGuia = false;
      if (this.loadingSubirArchivoGuia) return;
      this.procesarArchivoGuia(event.dataTransfer.files[0]);
    },
    formatearPeso(bytes) {
      if (!bytes) return '';
      const mb = bytes / (1024 * 1024);
      return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
    },
    // Las fotos de celular pesan varios MB y hacen que la subida falle por red.
    // Se reescalan a 1600px y se recomprimen antes de enviarlas.
    comprimirImagen(file) {
      return new Promise((resolve) => {
        const MAX_LADO = 1600;
        const url = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
          try {
            let { width, height } = img;
            if (width > MAX_LADO || height > MAX_LADO) {
              const escala = MAX_LADO / Math.max(width, height);
              width = Math.round(width * escala);
              height = Math.round(height * escala);
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);

            canvas.toBlob(
              (blob) => {
                URL.revokeObjectURL(url);
                if (!blob || blob.size >= file.size) {
                  resolve(file);
                  return;
                }
                const nombre = file.name.replace(/\.[^.]+$/, '') + '.jpg';
                resolve(new File([blob], nombre, { type: 'image/jpeg' }));
              },
              'image/jpeg',
              0.7,
            );
          } catch (e) {
            URL.revokeObjectURL(url);
            resolve(file);
          }
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(file);
        };

        img.src = url;
      });
    },
    async procesarArchivoGuia(file) {
      if (!file) return;

      const MAX_MB = 20;
      if (file.size > MAX_MB * 1024 * 1024) {
        Swal.fire({
          icon: 'warning',
          title: 'Archivo muy pesado',
          text: `El archivo pesa ${this.formatearPeso(file.size)}. El máximo permitido es ${MAX_MB} MB.`,
        });
        return;
      }

      let archivoFinal = file;
      if (this.esImagenComprimible(file)) {
        this.comprimiendoArchivoGuia = true;
        try {
          archivoFinal = await this.comprimirImagen(file);
        } finally {
          this.comprimiendoArchivoGuia = false;
        }
      }

      if (this.archivoGuia.url) {
        URL.revokeObjectURL(this.archivoGuia.url);
      }

      this.archivoGuia = {
        ...this.archivoGuia,
        archivo: archivoFinal,
        nombre: archivoFinal.name,
        url: URL.createObjectURL(archivoFinal),
      };
      this.pesoArchivoGuia = this.formatearPeso(archivoFinal.size);
      this.progresoSubidaGuia = 0;
    },
    async guardarArchivoGuia() {
      if (this.loadingSubirArchivoGuia) return;

      if (!this.archivoGuia.archivo) {
        Swal.fire({
          icon: 'warning',
          title: 'Campo requerido',
          text: 'Debes seleccionar un archivo.',
        });
        return;
      }

      this.loadingSubirArchivoGuia = true;
      this.progresoSubidaGuia = 0;

      const detalleSubido = this.detalleGuiaSeleccionado;

      const formData = new FormData();
      formData.append('envio_id', this.envioSeleccionadoGuias.id_envio_local);
      formData.append('tipo', this.archivoGuia.tipo);
      formData.append('archivo', this.archivoGuia.archivo);
      formData.append('comentario', this.archivoGuia.comentario);
      formData.append('id_estados_envio_local', 8);
      formData.append('id_envio_local_detalle', detalleSubido.id);

      // Permite distinguir si se traba enviando bytes (red) o esperando al servidor.
      let bytesEnviados = false;
      const inicio = Date.now();

      // Evita que el celular suspenda la subida al apagarse la pantalla.
      const wakeLock = await this.mantenerPantallaActiva();

      try {
        const data = await this.postConReintentos(formData, {
          onProgreso: (porcentaje, completo) => {
            this.progresoSubidaGuia = porcentaje;
            if (completo) bytesEnviados = true;
          },
          onReintento: (intento) => {
            bytesEnviados = false;
            this.intentoSubidaGuia = intento;
          },
        });

        console.log(`[subir-archivo-local] OK en ${((Date.now() - inicio) / 1000).toFixed(1)}s`);

        // Se refleja el archivo en la fila al instante, sin esperar a recargar el listado.
        if (data && data.archivo_url) {
          detalleSubido.archivo_url = data.archivo_url;
        }

        this.cerrarModalArchivoGuia();

        Swal.fire({
          icon: 'success',
          title: 'Archivo subido',
          text: 'El archivo se ha subido correctamente.',
          timer: 2000,
          showConfirmButton: false,
        });

        // El listado se refresca en segundo plano para no bloquear al usuario.
        this.refrescarEnvioSeleccionadoGuias();
      } catch (error) {

        const segundos = ((Date.now() - inicio) / 1000).toFixed(1);
        console.error(`[subir-archivo-local] Falló tras ${segundos}s. Bytes enviados: ${bytesEnviados}. Progreso: ${this.progresoSubidaGuia}%`, error);

        let mensaje = '';

        if (error.code === 'ECONNABORTED') {
          mensaje = bytesEnviados
            ? 'El archivo se envió completo pero el servidor no respondió a tiempo. Revisa en la lista si el cargo ya quedó registrado antes de volver a subirlo.'
            : 'La subida se quedó a medias por la conexión. Acércate a una señal mejor e inténtalo nuevamente.';
        } else if (error.response) {
          mensaje = error.response.status === 413
            ? 'El archivo es demasiado pesado para el servidor. Intenta con uno más liviano.'
            : (error.response.data?.message || JSON.stringify(error.response.data));
        } else if (error.request) {
          mensaje = 'No hubo respuesta del servidor. Revisa tu conexión a internet.';
        } else {
          mensaje = error.message;
        }

        Swal.fire({
          icon: 'error',
          title: 'No se pudo subir el archivo',
          text: mensaje,
        });

      } finally {
        this.loadingSubirArchivoGuia = false;
        this.progresoSubidaGuia = 0;
        this.intentoSubidaGuia = 0;
        if (wakeLock) {
          wakeLock.release().catch(() => {});
        }
      }
    },
    // Reintenta solo cuando la subida se cortó antes de enviar todo el archivo.
    // Si los bytes ya salieron completos, no se reintenta para no duplicar el cargo.
    async postConReintentos(formData, { onProgreso, onReintento }) {
      // El hosting ingesta las subidas muy lento (~32 Kbps medidos), asi que
      // conviene esperar a que termine antes que cortarla y reintentar desde
      // cero: no hay subida reanudable.
      const MAX_INTENTOS = this.maxIntentosSubida;
      let ultimoError;

      for (let intento = 1; intento <= MAX_INTENTOS; intento++) {
        let completo = false;

        if (intento > 1) onReintento(intento);

        try {
          const { data } = await axios.post('/subir-archivo-local', formData, {
            timeout: 600000,
            onUploadProgress: (e) => {
              if (!e.total) return;
              const porcentaje = Math.round((e.loaded * 100) / e.total);
              completo = porcentaje >= 100;
              onProgreso(porcentaje, completo);
            },
          });
          return data;
        } catch (error) {
          ultimoError = error;

          const esFalloDeRed = error.code === 'ECONNABORTED' || (!error.response && error.request);
          // Si ya se enviaron todos los bytes, el servidor pudo haberlo guardado.
          const seguroReintentar = esFalloDeRed && !completo && intento < MAX_INTENTOS;

          if (!seguroReintentar) throw error;

          console.warn(`[subir-archivo-local] Intento ${intento} falló, reintentando...`, error.code || error.message);
          await new Promise((r) => setTimeout(r, 1500 * intento));
        }
      }

      throw ultimoError;
    },
    async mantenerPantallaActiva() {
      try {
        if ('wakeLock' in navigator) {
          return await navigator.wakeLock.request('screen');
        }
      } catch (e) {
        console.warn('No se pudo mantener la pantalla activa', e);
      }
      return null;
    },
    async refrescarEnvioSeleccionadoGuias() {
      try {
        await this.buscarEnvios(true);

        if (!this.envioSeleccionadoGuias) return;

        const envioActualizado = this.envios.find(
          envio => envio.id_envio_local === this.envioSeleccionadoGuias.id_envio_local
        );
        if (envioActualizado) {
          this.envioSeleccionadoGuias = envioActualizado;
        }
      } catch (error) {
        console.error('Error al refrescar el envío tras subir archivo', error);
      }
    },
    obtenerNombreEmpresa(id) {
      return this.empresas.find((e) => e.id === id)?.nombre || 'No especificado'
    },
    obtenerTipoUnidad(id) {
      return (
        this.tipoUnidades.find((t) => t.id === id)?.tipo_unidad ||
        'No especificado'
      )
    },
    async buscarEnvios(silencioso = false) {
      if (!silencioso) {
        this.isLoading = true;
      }

      try {
        const { data } = await axios.get('envios-locales', { params: { ...this.filtros, page: this.pagina } });
        // Si la página quedó fuera de rango (p. ej. al eliminar el último envío de la última página), ir a la última
        if (data.data.length === 0 && data.total > 0 && this.pagina > data.last_page) {
          this.pagina = data.last_page;
          return this.buscarEnvios(silencioso);
        }
        this.envios = data.data;
        this.totalPaginas = data.last_page;
        this.totalEnvios = data.total;
        console.log("envios", this.envios)
      } catch (error) {
        if (silencioso) throw error;
        if (error.response && error.response.status === 500) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Error interno del servidor. Intente nuevamente más tarde.',
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Ocurrió un error al buscar los envíos.',
          });
        }
        console.error('Error al buscar envíos', error);
      } finally {
        this.isLoading = false;
      }
    },
    aplicarFiltros() {
      this.pagina = 1;
      this.buscarEnvios();
    },
    cambiarPagina(n) {
      this.pagina = n;
      this.buscarEnvios();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    async aceptarEnvio() {
      await this.actualizarEnvio('aceptar')
    },

    async rechazarEnvio() {
      await this.actualizarEnvio('rechazar')
    },
    async actualizarEnvio(accion) {
      try {
        await axios.get(`${accion}-envio/${this.detalleEnvio.id_envio}`)
        this.cerrarDetalle()
        this.buscarEnvios()
        Swal.fire({
          icon: 'success',
          title: 'Envío Actualizado',
          text: 'Los datos se han actualizado correctamente.',
        })
      } catch (error) {
        console.error(`Error al ${accion} envío`, error)
      }
    },
    getBadgeClass(estado) {
      const badgeMap = {
        1: 'bg-secondary', // Solicitud Recibida
        2: 'bg-success',   // Aprobada
        3: 'bg-info',    // Programado para ruta
        4: 'bg-warning',   // En espera
        5: 'bg-info',      // Recojo en curso
        6: 'bg-success',   // Recojo confirmado
        7: 'bg-warning',   // Embarque/Despacho
        8: 'bg-info',      // Traslado en curso
        9: 'bg-primary',   // Llegada a destino
        10: 'bg-info',     // Entrega en curso
        11: 'bg-success',  // Entregado
        12: 'bg-primary',  // Cargo PDF
        13: 'bg-danger',  // Cargo Físico Confirmado
        14: 'bg-warning',   // Servicio Reprogramado(ANW)
        15: 'bg-danger',  // Cancelado        
      };

      return `badge ${badgeMap[estado] || 'bg-secondary'} breakable-badge`;
    },
    limpiarFiltros() {
      Object.keys(this.filtros).forEach(
        (key) =>
          (this.filtros[key] = ''),
      )
      this.filtros.tipo_paquete = "mercaderia",
        this.pagina = 1,
        this.buscarEnvios()
    },

    abrirCargarArchivosModal(envio) {
      this.envioId = envio.id_envio_local
      this.cargarArchivos(envio.id_envio_local)
      this.showModalArchivos = true
    },
    seleccionarArchivo(event) {
      this.procesarArchivo(event.target.files[0])
      event.target.value = ''
    },
    abrirExplorador() {
      if (this.loadingActualizarEstado || this.comprimiendoArchivo) return;
      // El input solo existe mientras no haya archivo elegido.
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click()
      }
    },
    soltarArchivo(event) {
      this.dragging = false
      if (this.loadingActualizarEstado) return;
      this.procesarArchivo(event.dataTransfer.files[0])
    },
    async procesarArchivo(file) {
      if (!file) return;

      const MAX_MB = 20;
      if (file.size > MAX_MB * 1024 * 1024) {
        Swal.fire({
          icon: 'warning',
          title: 'Archivo muy pesado',
          text: `El archivo pesa ${this.formatearPeso(file.size)}. El maximo permitido es ${MAX_MB} MB.`,
        });
        return;
      }

      // Las fotos de celular pesan varios MB y la subida al hosting es lenta.
      let archivoFinal = file;
      if (this.esImagenComprimible(file)) {
        this.comprimiendoArchivo = true;
        try {
          archivoFinal = await this.comprimirImagen(file);
        } finally {
          this.comprimiendoArchivo = false;
        }
      }

      if (this.nuevoArchivo.url) {
        URL.revokeObjectURL(this.nuevoArchivo.url);
      }

      this.nuevoArchivo = {
        ...this.nuevoArchivo,
        archivo: archivoFinal,
        nombre: archivoFinal.name,
        url: URL.createObjectURL(archivoFinal),
      };
      this.pesoArchivo = this.formatearPeso(archivoFinal.size);
    },


    async cargarArchivos(envioId) {
      try {
        const { data } = await axios.get(`/archivos-local/${envioId}`)
        this.archivos = data
      } catch (error) {
        console.error('Error al obtener archivos', error)
      }
    },
    verArchivo(archivo) {
      window.open(archivo.url, '_blank')
    },
    verArchivoGuia(archivoUrl) {
      window.open(archivoUrl, '_blank')
    },
    async eliminarArchivo(id) {
      console.log("archivo_id", id)
      const result = await Swal.fire({
        title: '¿Estás seguro?',
        text: 'Esta acción no se puede deshacer.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
      })

      if (result.isConfirmed) {
        try {
          await axios.delete(`/eliminar-archivo-local/${id}`)
          this.cargarArchivos(this.envioId).then(r => {
            console.log("r", r)
            Swal.fire({
              icon: 'success',
              title: 'Eliminado',
              text: 'El archivo ha sido eliminado correctamente.',
              confirmButtonText: 'OK',
            })
          })
        } catch (error) {
          console.error('Error al eliminar archivo', error)

          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo eliminar el archivo.',
            confirmButtonText: 'OK',
          })
        }
      }
    },

    esImagen(nombre) {
      return /\.(jpg|jpeg|png|gif)$/i.test(nombre)
    },
    // No basta con mirar file.type: varios selectores de galeria de Android
    // devuelven el archivo con type vacio o application/octet-stream, y esas
    // fotos se subirian sin comprimir. Por eso tambien se mira la extension.
    esImagenComprimible(file) {
      if (file.type && file.type.startsWith('image/')) return true;
      if (!file.type || file.type === 'application/octet-stream') {
        return /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(file.name || '');
      }
      return false;
    },
  },
}
</script>




<style scoped>
.modal.fade.show {
  display: block;
}

.modal.show {
  background: rgba(0, 0, 0, 0.5);
}


.badge {
  font-size: 10px;
  padding: 8px 8px;
  border-radius: 5px;
  line-height: 1.2;
}

.breakable-badge {
  white-space: normal;
  word-break: break-word;
}

.table-font-small {
  font-size: 0.75rem;
}



.table th,
.table td {
  white-space: normal;
  /* Permite saltos de línea */
  /* Oculta el exceso de contenido */
  text-overflow: ellipsis;
  /* Mantiene los puntos suspensivos si el contenido es demasiado grande */
  word-wrap: break-word;
  /* Rompe palabras largas si es necesario */
  overflow-wrap: break-word;
  /* Alternativa moderna */
  text-align: center;
  vertical-align: middle;
}



@media (max-width:768px) {

  .fecha-mobile {
    min-width: 75px;
  }

  .cliente-mobile {
    min-width: 145px;
  }

}

@media (min-width: 768px) {
  .table {
    width: 100%;
  }

  .table-responsive {
    overflow: visible !important;
    /* Permite que el dropdown no se esconda */
  }
}

.dropdown-menu {
  min-width: 180px;
}

.dropdown-item i {
  margin-right: 8px;
}

.custom-dropdown {
  position: absolute !important;
  /* Permite que se desplace sobre otras filas */
  z-index: 1050 !important;
  /* Asegura que esté sobre otros elementos */
  left: auto !important;
  /* Evita que se recorte en la tabla */
  right: 0 !important;
  /* Alinea correctamente */
}



.dropdown-menu.show {
  position: absolute !important;
  z-index: 1050 !important;
  will-change: transform;
}

.dropzone {
  border: 2px dashed #007bff;
  padding: 20px;
  text-align: center;
  border-radius: 10px;
  cursor: pointer;
  transition: 0.3s;
}

.dropzone:hover,
.dropzone.drop-hover {
  background: rgba(0, 123, 255, 0.1);
}

.dropzone-content i {
  color: #007bff;
}

.dropzone-preview img {
  max-width: 100px;
  margin-top: 10px;
}

.swal2-container {
  z-index: 9999 !important;
}

.modal {
  z-index: 90;
}

.table-responsive {
  min-height: 250px;
}

.badge:hover {
  cursor: pointer;
}

/* Detalle */
</style>
